import { useMemo } from 'react';
import type { Official, User, RoleAssignment, Match, Payment } from '../types';
import { UserRole } from '../types';
import { getResolvedRole } from '../lib/permissions/resolver';
import type {
  PermissionAction,
  PermissionResource,
  PermissionRule,
  PermissionScope,
  PermissionContext,
} from '../lib/permissions/types';

export type Action = PermissionAction;
export type Resource = PermissionResource;

interface ResolvedAssignment {
  assignment: RoleAssignment & { leagueIds: string[] };
  roleKey: string;
  scopeLeagues: string[]; // Normalised list of league IDs for scope evaluation
  grants: PermissionRule[];
}

export interface Permissions {
  can: (action: Action, resource: Resource, subject?: unknown) => boolean;
  canAny: (actions: Action[], resource: Resource, subject?: unknown) => boolean;
  allowedActions: (resource: Resource, subject?: unknown) => Action[];
  roleAssignments: RoleAssignment[];
}

const ALL_KNOWN_ACTIONS: Action[] = [
  'view',
  'list',
  'create',
  'edit',
  'update',
  'delete',
  'archive',
  'assign',
  'unassign',
  'send',
  'submit',
  'approve',
  'reject',
  'validate',
  'publish',
  'close',
  'reopen',
  'escalate',
  'comment',
  'confirm',
  'cancel',
  'withdraw',
  'notify',
  'remind',
  'scan',
  'manage',
  'export',
  'download',
  'edit_role',
  'impersonate',
  'submit_accounting',
  'validate_accounting',
  'reject_accounting',
  'close_accounting_period',
  'reopen_accounting_period',
  'transfer',
  'calculate',
  'optimize',
  'sync',
  'bulk_action',
];

const UNIQUE = <T,>(values: (T | null | undefined)[]): T[] => {
  const set = new Set<T>();
  values.forEach((value) => {
    if (value !== undefined && value !== null) {
      set.add(value);
    }
  });
  return Array.from(set.values());
};

const normaliseAssignments = (user: User | null): RoleAssignment[] => {
  if (!user) return [];

  if (user.roles && user.roles.length > 0) {
    return user.roles;
  }

  return [
    {
      role: (user.primaryRole || user.role || UserRole.VIEWER) as string,
      leagueIds: user.leagueIds,
      isPrimary: true,
    },
  ];
};

const resolveScopeLeagues = (assignment: RoleAssignment, user: User | null): string[] => {
  const explicit = assignment.leagueIds || [];
  if (explicit.length > 0) {
    return explicit;
  }
  return user?.leagueIds || [];
};

const extractLeagueIdsFromSubject = (resource: Resource, subject: unknown): string[] => {
  if (!subject) return [];

  const inspectables = Array.isArray(subject) ? subject : [subject];

  const extracted = inspectables.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const candidate = item as Record<string, any>;

    if (candidate.leagueId) return [candidate.leagueId];
    if (candidate.league_id) return [candidate.league_id];

    if (resource === 'match' || resource === 'matches' || candidate.match) {
      const match: Partial<Match> = (candidate.match ?? candidate) as Partial<Match>;
      const leagueFromGroup = (match.leagueGroup as any)?.league?.id;
      const leagueDirect = (match.leagueGroup as any)?.league_id;
      if (leagueFromGroup || leagueDirect) return UNIQUE([leagueFromGroup, leagueDirect]);
    }

    if (resource === 'accounting' || resource === 'accounting_period' || resource === 'payment' || resource === 'finances') {
      const payment = candidate as Partial<Payment>;
      if (payment.leagueId) return [payment.leagueId];
      if ((payment as any).league_id) return [(payment as any).league_id];
      const match: Partial<Match> | undefined = (payment as any).match as Partial<Match> | undefined;
      if (match) {
        return extractLeagueIdsFromSubject('match', match);
      }
    }

    if (candidate.league?.id) return [candidate.league.id];
    if (candidate.league?.league_id) return [candidate.league.league_id];

    if (candidate.leagueGroup) {
      return extractLeagueIdsFromSubject('league_group', candidate.leagueGroup);
    }

    if (resource === 'league_group' && candidate.league_id) {
      return [candidate.league_id];
    }

    // Handle teams, players, staff, stadiums (they have leagueId or league_id)
    if (['team', 'teams', 'player', 'players', 'staff', 'stadium', 'stadiums'].includes(resource)) {
      const ids = [];
      if (candidate.leagueId) ids.push(candidate.leagueId);
      if (candidate.league_id) ids.push(candidate.league_id);
      if (candidate.league?.id) ids.push(candidate.league.id);
      return ids;
    }

    // Handle disciplinary resources
    if (['disciplinary', 'disciplinary_case', 'disciplinary_session', 'sanction', 'sanctions', 'suspension', 'suspensions'].includes(resource)) {
      const ids = [];
      if (candidate.leagueId) ids.push(candidate.leagueId);
      if (candidate.league_id) ids.push(candidate.league_id);
      return ids;
    }

    return [];
  });

  return UNIQUE(extracted);
};

const defaultOwnGuard = (resource: Resource, subject: unknown, user: User | null, extras: Record<string, unknown>): boolean => {
  if (!user || !subject) return false;

  const inspectables = Array.isArray(subject) ? subject : [subject];

  const officials = (extras.officials as Official[]) || [];

  return inspectables.every((item) => {
    if (!item || typeof item !== 'object') return false;
    const candidate = item as Record<string, any>;

    if (candidate.userId) {
      return candidate.userId === user.id;
    }

    if (candidate.user_id) {
      return candidate.user_id === user.id;
    }

    if (resource === 'match' || resource === 'matches') {
      const match = candidate as Partial<Match>;
      const assignments = (match.assignments || []) as any[];
      return assignments.some((assignment) => {
        if (!assignment.officialId) return false;
        const official = officials.find((o) => o.id === assignment.officialId);
        return !!official && official.userId === user.id;
      });
    }

    if (resource === 'payment' || resource === 'finances') {
      const payment = candidate as Partial<Payment>;
      if (payment.officialId) {
        const official = officials.find((o) => o.id === payment.officialId);
        return !!official && official.userId === user.id;
      }
    }

    if ((resource === 'availability' || resource === 'availabilities') && candidate.officialId) {
      const official = officials.find((o) => o.id === candidate.officialId);
      return !!official && official.userId === user.id;
    }

    if (resource === 'expense_report' && candidate.officialId) {
      const official = officials.find((o) => o.id === candidate.officialId);
      return !!official && official.userId === user.id;
    }

    if ((resource === 'notification' || resource === 'notifications' || resource === 'email' || resource === 'emails') && candidate.userId) {
      return candidate.userId === user.id;
    }

    return false;
  });
};

const isAllMarker = (values: readonly unknown[]): values is ['*'] => values.length === 1 && values[0] === '*';

const matchesResource = (grant: PermissionRule, resource: Resource): boolean => {
  if (isAllMarker(grant.resources as any)) {
    return true;
  }
  return (grant.resources as Resource[]).includes(resource);
};

const matchesAction = (grant: PermissionRule, action: Action): boolean => {
  if (isAllMarker(grant.actions as any)) {
    return true;
  }
  return (grant.actions as Action[]).includes(action);
};

const isSuperAdmin = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === UserRole.SUPER_ADMIN || user.primaryRole === UserRole.SUPER_ADMIN) {
    return true;
  }
  return (user.roles || []).some((assignment) => assignment.role === UserRole.SUPER_ADMIN);
};

const evaluateScope = (
  scope: PermissionScope | undefined,
  context: PermissionContext,
  extras: Record<string, unknown>,
): boolean => {
  const effectiveScope = scope || 'global';

  switch (effectiveScope) {
    case 'global':
      return true;
    case 'assigned-leagues': {
      const allowedLeagues = context.assignment.leagueIds && context.assignment.leagueIds.length > 0
        ? context.assignment.leagueIds
        : context.user.leagueIds || [];

      if ((allowedLeagues || []).length === 0) {
        return false;
      }

      if (context.extractedLeagueIds.length === 0) {
        // SECURITY FIX: Deny access when no leagues extracted FROM A PROVIDED SUBJECT
        // This prevents access to resources that exist but failed league ID extraction
        if (context.subject !== undefined) {
          return false;
        }
        // For general permission checks (no subject), allow if user has assigned leagues
        // This fixes inheritance for roles like LEAGUE_PRESIDENT
        return (allowedLeagues || []).length > 0;
      }

      return context.extractedLeagueIds.every((leagueId) => allowedLeagues!.includes(leagueId));
    }
    case 'own':
      return defaultOwnGuard(context.resource, context.subject, context.user, extras);
    case 'custom':
      return true;
    default:
      return false;
  }
};

export const usePermissions = (user: User | null, officials: Official[] = []): Permissions => {
  return useMemo(() => {
    const assignments = normaliseAssignments(user);
    const resolvedAssignments: ResolvedAssignment[] = assignments
      .map<ResolvedAssignment | null>((assignment) => {
        const resolvedRole = getResolvedRole(assignment.role as string);
        if (!resolvedRole) return null;

        const fallbackLeagues = resolveScopeLeagues(assignment, user);
        const enrichedAssignment: RoleAssignment & { leagueIds: string[] } = {
          ...assignment,
          leagueIds: assignment.leagueIds && assignment.leagueIds.length > 0 ? assignment.leagueIds : fallbackLeagues,
        };

        return {
          assignment: enrichedAssignment,
          roleKey: resolvedRole.key,
          scopeLeagues: fallbackLeagues,
          grants: resolvedRole.resolvedGrants,
        };
      })
      .filter((value): value is ResolvedAssignment => value !== null);

    const extras = { officials } as Record<string, unknown>;

    const evaluate = (action: Action, resource: Resource, subject?: unknown): boolean => {
      if (!user) return false;
      if (isSuperAdmin(user)) return true;
      let subjectLeagueIds: string[] = [];
      if (subject) {
        subjectLeagueIds = extractLeagueIdsFromSubject(resource, subject);
      }
      else {
        subjectLeagueIds = user.leagueIds || [];
      }
      
      for (const resolved of resolvedAssignments) {
        for (const grant of resolved.grants) {
          if (!matchesResource(grant, resource)) continue;
          if (!matchesAction(grant, action)) continue;

          const context: PermissionContext = {
            user,
            assignment: resolved.assignment,
            action,
            resource,
            subject,
            extractedLeagueIds: subjectLeagueIds,
            extras,
          };

          if (!evaluateScope(grant.scope, context, extras)) {
            continue;
          }

          if (grant.guard && !grant.guard(context)) {
            continue;
          }

          return true;
        }
      }

      return false;
    };

    const can = (action: Action, resource: Resource, subject?: unknown) => evaluate(action, resource, subject);

    const canAny = (actions: Action[], resource: Resource, subject?: unknown) =>
      actions.some((action) => evaluate(action, resource, subject));

    const allowedActions = (resource: Resource, subject?: unknown): Action[] => {
      const uniqueActions = new Set<Action>();
      for (const resolved of resolvedAssignments) {
        for (const grant of resolved.grants) {
          if (!matchesResource(grant, resource)) continue;
          if (isAllMarker(grant.actions as any)) {
            ALL_KNOWN_ACTIONS.forEach((candidate) => {
              if (evaluate(candidate, resource, subject)) {
                uniqueActions.add(candidate);
              }
            });
          } else {
            (grant.actions as Action[]).forEach((action) => {
              if (evaluate(action, resource, subject)) {
                uniqueActions.add(action);
              }
            });
          }
        }
      }
      return Array.from(uniqueActions.values());
    };

    return {
      can,
      canAny,
      allowedActions,
      roleAssignments: assignments,
    };
  }, [user, officials]);
};